/**
 * Workflow Canvas Component
 *
 * React Flow canvas for the workflow editor.
 */

import React, { useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useReactFlow,
  type NodeMouseHandler,
  type Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes, nodeConfig } from './nodes';
import { useWorkflowStore } from '../hooks/useWorkflowStore';
import type { NodeType } from '@/types/imageWorkflow';

interface WorkflowCanvasProps {
  onNodeClick?: (nodeId: string) => void;
  readOnly?: boolean;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  onNodeClick,
  readOnly = false,
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useReactFlow();

  // Zustand store
  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const onNodesChange = useWorkflowStore((s) => s.onNodesChange);
  const onEdgesChange = useWorkflowStore((s) => s.onEdgesChange);
  const onConnect = useWorkflowStore((s) => s.onConnect);
  const setViewport = useWorkflowStore((s) => s.setViewport);
  const addNode = useWorkflowStore((s) => s.addNode);
  const selectNode = useWorkflowStore((s) => s.selectNode);
  const setEditorReady = useWorkflowStore((s) => s.setEditorReady);

  // Handle viewport change
  const handleViewportChange = useCallback(
    (viewport: Viewport) => {
      setViewport({
        x: viewport.x,
        y: viewport.y,
        zoom: viewport.zoom,
      });
    },
    [setViewport]
  );

  // Handle node click
  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      selectNode(node.id);
      onNodeClick?.(node.id);
    },
    [selectNode, onNodeClick]
  );

  // Handle pane click (deselect)
  const handlePaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  // Handle drag over (for drag-and-drop from toolbar)
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop (for drag-and-drop from toolbar)
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (readOnly) return;

      const type = event.dataTransfer.getData('application/workflow-node') as NodeType;
      if (!type) return;

      // Use screenToFlowPosition to properly convert screen coordinates to flow coordinates
      // This accounts for current viewport zoom and pan position
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Offset to center the node on the cursor (approximate node dimensions)
      // Nodes are typically around 180x80 based on the node styling
      const centeredPosition = {
        x: position.x - 90,
        y: position.y - 40,
      };

      addNode(type, centeredPosition);
    },
    [addNode, readOnly, reactFlowInstance]
  );

  // Mark editor as ready on init
  const handleInit = useCallback(() => {
    setEditorReady(true);
  }, [setEditorReady]);

  return (
    <div
      ref={reactFlowWrapper}
      className="h-full min-h-0 w-full bg-gray-50"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onViewportChange={handleViewportChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onInit={handleInit}
        nodeTypes={nodeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        deleteKeyCode={readOnly ? null : 'Backspace'}
        selectionKeyCode={readOnly ? null : 'Shift'}
        multiSelectionKeyCode={readOnly ? null : 'Meta'}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
      >
        <Background color="#e5e7eb" gap={15} />
        <Controls showInteractive={!readOnly} />
        <MiniMap
          nodeStrokeWidth={3}
          nodeColor={(node) => {
            const config = nodeConfig[node.type as NodeType];
            return config?.borderColor.replace('border-', '').replace('-300', '') || '#999';
          }}
        />

        {/* Empty state panel */}
        {nodes.length === 0 && (
          <Panel position="top-center" className="mt-20">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-gray-400 mb-2">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">
                Empty Workflow
              </h3>
              <p className="text-sm text-gray-500">
                Drag nodes from the toolbar to get started
              </p>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
};

export default WorkflowCanvas;
